import { api, unwrap } from "@lumberset/api-client";
import { money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  Switch,
  Table,
  Textarea,
  toast,
} from "@lumberset/ui";
import { CreditCard, Plus } from "@medusajs/icons";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { centsToDollars, dollarsToCents, wholeNumber } from "../../lib/money";
import { billingPlansQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/billing-plans")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(billingPlansQuery),
  component: BillingPlans,
});

type BillingPlan = Awaited<
  ReturnType<NonNullable<typeof billingPlansQuery.queryFn>>
>["billingPlans"][number];

const SLUG_PATTERN = "[a-z0-9-]{2,40}";

function BillingPlans() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(billingPlansQuery);
  const [editing, setEditing] = useState<BillingPlan | "new" | null>(null);
  const [promoting, setPromoting] = useState<BillingPlan | null>(null);

  const currentDefault = data.billingPlans.find((billingPlan) => billingPlan.isDefault);

  const makeDefault = useMutation({
    mutationFn: (billingPlan: BillingPlan) =>
      unwrap(
        api.admin["billing-plans"][":id"]["make-default"].$post({ param: { id: billingPlan.id } }),
      ),
    onSuccess: async ({ billingPlan }) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "billing-plans"] });
      toast.success(`${billingPlan.name} is now the default Billing Plan`);
      setPromoting(null);
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <>
      <Crumbs trail={[{ label: "Billing Plans" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Billing Plans"
          description="What a workspace pays: a monthly fee, the quotes it includes, and the Quote Fee beyond them."
          actions={
            <Button size="small" onClick={() => setEditing("new")}>
              <Plus /> New Billing Plan
            </Button>
          }
        />

        {data.billingPlans.length === 0 ? (
          <EmptyState
            icon={<CreditCard />}
            title="No Billing Plans yet"
            description="Create one and make it the default, so new workspaces have somewhere to start."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Billing Plan</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Monthly fee</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Included quotes</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Quote Fee</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Change Order Fee</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Trial quotes</Table.HeaderCell>
                <Table.HeaderCell>Public</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Workspaces</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">Actions</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.billingPlans.map((billingPlan) => (
                <Table.Row
                  key={billingPlan.id}
                  data-clickable="true"
                  onClick={() => setEditing(billingPlan)}
                >
                  <Table.Cell>
                    <div className="flex items-center gap-x-2">
                      <span className="txt-compact-small-plus text-ui-fg-base">
                        {billingPlan.name}
                      </span>
                      {billingPlan.isDefault ? <Badge color="green">Default</Badge> : null}
                      {billingPlan.active ? null : <Badge color="orange">Inactive</Badge>}
                    </div>
                    <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                      {billingPlan.slug}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {money(billingPlan.monthlyFeeCents)}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {billingPlan.includedQuotes}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                    {money(billingPlan.quoteFeeCents)}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {billingPlan.changeOrderFeeCents === 0
                      ? "Free"
                      : money(billingPlan.changeOrderFeeCents)}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">{billingPlan.trialQuotes}</Table.Cell>
                  <Table.Cell>{billingPlan.isPublic ? "Yes" : "No"}</Table.Cell>
                  <Table.Cell className="tabular text-right">{billingPlan.yards}</Table.Cell>
                  <Table.Cell className="w-[1%] whitespace-nowrap text-right">
                    {billingPlan.isDefault ? null : (
                      <Button
                        size="small"
                        variant="secondary"
                        disabled={!billingPlan.active}
                        onClick={(event) => {
                          event.stopPropagation();
                          setPromoting(billingPlan);
                        }}
                      >
                        Make default
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <BillingPlanDrawer editing={editing} onClose={() => setEditing(null)} />

      <Prompt open={promoting !== null} onOpenChange={(open) => !open && setPromoting(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Make {promoting?.name} the default Billing Plan?</Prompt.Title>
            <Prompt.Description>
              Every new workspace will start on it.
              {currentDefault
                ? ` ${currentDefault.name} stops being the default. Workspaces already on a Billing Plan stay where they are.`
                : ""}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep things as they are</Prompt.Cancel>
            <Button
              size="small"
              isLoading={makeDefault.isPending}
              onClick={() => promoting && makeDefault.mutate(promoting)}
            >
              Make default
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}

function BillingPlanDrawer({
  editing,
  onClose,
}: {
  editing: BillingPlan | "new" | null;
  onClose: () => void;
}) {
  const existing = editing !== "new" ? editing : null;
  return (
    <Drawer open={editing !== null} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content>
        {editing !== null ? (
          <BillingPlanForm key={existing?.id ?? "new"} existing={existing} onClose={onClose} />
        ) : null}
      </Drawer.Content>
    </Drawer>
  );
}

/** Creates or edits one Billing Plan. Keyed by its id so state starts fresh each time. */
function BillingPlanForm({
  existing,
  onClose,
}: {
  existing: BillingPlan | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [isPublic, setIsPublic] = useState(existing?.isPublic ?? true);
  const [active, setActive] = useState(existing?.active ?? true);

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      const json = {
        slug: text("slug"),
        name: text("name"),
        description: text("description") || null,
        monthlyFeeCents: dollarsToCents(form.get("monthlyFee")) ?? 0,
        includedQuotes: wholeNumber(form.get("includedQuotes")),
        quoteFeeCents: dollarsToCents(form.get("quoteFee")) ?? 0,
        changeOrderFeeCents: dollarsToCents(form.get("changeOrderFee")) ?? 0,
        trialQuotes: Math.min(100, wholeNumber(form.get("trialQuotes"))),
        features: text("features")
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .slice(0, 20),
        isPublic,
        active,
        position: wholeNumber(form.get("position")),
      };
      return existing
        ? unwrap(api.admin["billing-plans"][":id"].$patch({ param: { id: existing.id }, json }))
        : unwrap(api.admin["billing-plans"].$post({ json }));
    },
    onSuccess: async ({ billingPlan }) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "billing-plans"] });
      toast.success(existing ? "Billing Plan saved" : "Billing Plan created", {
        description: billingPlan.name,
      });
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{existing ? existing.name : "New Billing Plan"}</Drawer.Title>
        <Drawer.Description>
          {existing
            ? `${existing.yards} ${existing.yards === 1 ? "workspace is" : "workspaces are"} on this Billing Plan. Changes apply to their next Billable Quote.`
            : "Money is in dollars here and stored as cents. Nothing is charged until a workspace is on the Billing Plan."}
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Name">
          {(control) => (
            <Input
              {...control}
              name="name"
              required
              maxLength={80}
              autoFocus
              defaultValue={existing?.name ?? ""}
            />
          )}
        </Field>

        <Field
          label="Slug"
          hint="Lowercase letters, digits and hyphens. It names the Billing Plan in links and the seed."
        >
          {(control) => (
            <Input
              {...control}
              name="slug"
              required
              pattern={SLUG_PATTERN}
              title="Lowercase letters, digits and hyphens, 2 to 40 characters."
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="pay-per-quote"
              defaultValue={existing?.slug ?? ""}
            />
          )}
        </Field>

        <Field label="Description" optional hint="One sentence for the pricing page.">
          {(control) => (
            <Textarea
              {...control}
              name="description"
              maxLength={400}
              rows={2}
              defaultValue={existing?.description ?? ""}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Monthly fee">
            {(control) => (
              <Input
                {...control}
                name="monthlyFee"
                inputMode="decimal"
                prefix="$"
                required
                pattern="[0-9]*\.?[0-9]{0,2}"
                defaultValue={centsToDollars(existing?.monthlyFeeCents ?? 0)}
              />
            )}
          </Field>
          <Field label="Included quotes">
            {(control) => (
              <Input
                {...control}
                name="includedQuotes"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                required
                defaultValue={existing?.includedQuotes ?? 0}
              />
            )}
          </Field>
          <Field label="Quote Fee">
            {(control) => (
              <Input
                {...control}
                name="quoteFee"
                inputMode="decimal"
                prefix="$"
                required
                pattern="[0-9]*\.?[0-9]{0,2}"
                defaultValue={centsToDollars(existing?.quoteFeeCents ?? 0)}
              />
            )}
          </Field>
          <Field label="Trial quotes">
            {(control) => (
              <Input
                {...control}
                name="trialQuotes"
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                step={1}
                required
                defaultValue={existing?.trialQuotes ?? 0}
              />
            )}
          </Field>
          <Field
            label="Change Order Fee"
            hint="Charged when revised drawings are read again after release. Zero makes change orders free."
          >
            {(control) => (
              <Input
                {...control}
                name="changeOrderFee"
                inputMode="decimal"
                prefix="$"
                required
                pattern="[0-9]*\.?[0-9]{0,2}"
                defaultValue={centsToDollars(existing?.changeOrderFeeCents ?? 10000)}
              />
            )}
          </Field>
        </div>

        <Field label="Features" optional hint="One per line, as shown on the pricing page.">
          {(control) => (
            <Textarea
              {...control}
              name="features"
              rows={5}
              defaultValue={existing?.features.join("\n") ?? ""}
              placeholder={"Unlimited Members\nEvery takeoff checked by our Reviewers"}
            />
          )}
        </Field>

        <Field label="Position" hint="Lower numbers come first on the pricing page.">
          {(control) => (
            <Input
              {...control}
              name="position"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              required
              defaultValue={existing?.position ?? 0}
              className="sm:max-w-[120px]"
            />
          )}
        </Field>

        <ul className="divide-y divide-ui-border-base rounded-lg shadow-borders-base">
          <li className="flex items-center justify-between gap-x-4 px-4 py-3">
            <label htmlFor="billing-plan-public" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">Public</span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                Shown on the pricing page and offered at signup.
              </span>
            </label>
            <Switch id="billing-plan-public" checked={isPublic} onCheckedChange={setIsPublic} />
          </li>
          <li className="flex items-center justify-between gap-x-4 px-4 py-3">
            <label htmlFor="billing-plan-active" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">Active</span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                An inactive Billing Plan keeps its workspaces but takes no new ones and cannot be
                the default.
              </span>
            </label>
            <Switch id="billing-plan-active" checked={active} onCheckedChange={setActive} />
          </li>
        </ul>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          {existing ? "Save" : "Create Billing Plan"}
        </Button>
      </Drawer.Footer>
    </form>
  );
}
